/**
 * Hydrate a portable PlaybackSession from host context (Sprint 6B / B3).
 * Organization identity comes from tenant config — not a hardcoded brand.
 */
import { createPlaybackSession } from "../Core/PlaybackSession";
import { PipelinePhase } from "../Core/PlaybackPipeline";
import { resolveMediaFromVideoDetails } from "./buildHostPlayerConfig";
import { resolveHostEntitlements } from "./resolveHostEntitlements";
import { resolveTenantOrganization } from "./resolveTenantOrganization";
import {
  resolvePlaybackExperience,
  getPrimaryIntroLayer,
} from "../../components/Helper/playbackExperience";

function readViewer() {
  if (typeof localStorage === "undefined") {
    return { userId: null, profileId: null, accessTokenPresent: false };
  }
  return {
    userId: localStorage.getItem("userId") || null,
    profileId: localStorage.getItem("active_profile_id") || "default",
    accessTokenPresent: !!localStorage.getItem("accessToken"),
  };
}

/**
 * @param {object} details video DTO
 * @param {object} [opts]
 * @param {boolean} [opts.isTrailer]
 * @param {string|null} [opts.redirectStatus]
 * @param {object} [opts.bus]
 * @param {object} [opts.organization]
 * @returns {object} PlaybackSession
 */
export function createHostPlaybackSession(details, opts) {
  opts = opts || {};
  details = details || {};
  var isTrailer = !!opts.isTrailer;
  var media = resolveMediaFromVideoDetails(details, {
    videoFrom: isTrailer ? "trailer" : undefined,
  });
  var pe = resolvePlaybackExperience(details, { isTrailer: isTrailer });
  var introLayer = getPrimaryIntroLayer(pe);
  var entitlements = resolveHostEntitlements(details, {
    redirectStatus: opts.redirectStatus,
    isTrailer: isTrailer,
  });

  var seek =
    !isTrailer && Number(details.seek_time_in_seconds) > 0
      ? Number(details.seek_time_in_seconds)
      : 0;

  var isLive = !!(details.is_live || details.live || media.type === "live");

  var session = createPlaybackSession({
    bus: opts.bus || null,
    viewer: readViewer(),
    organization: Object.assign(
      resolveTenantOrganization(),
      opts.organization || {}
    ),
    content: {
      contentId: details.admin_video_id != null ? String(details.admin_video_id) : null,
      title: details.title || details.name || null,
      mediaKind: isTrailer ? "trailer" : isLive ? "live" : "vod",
      presentationKind:
        (details.media_asset && details.media_asset.presentationKind) ||
        (details.media_asset && details.media_asset.presentation_kind) ||
        null,
      sourceUrl: media && media.src ? media.src : null,
      poster: media && media.poster ? media.poster : null,
      durationHint: details.duration || null,
    },
    entitlements: entitlements,
    intro: {
      active: !!(introLayer && introLayer.url && !isTrailer),
      layerId: introLayer && (introLayer.id || introLayer.key) || null,
      url: introLayer && introLayer.url || null,
      skipVisible: false,
      completed: false,
    },
    resume: {
      position: seek,
      source: seek > 0 ? "server" : "none",
    },
    experience: {
      policyId: pe && (pe.policy_id || pe.id) || null,
      layers: (pe && pe.layers) || [],
    },
    live: {
      isLive: isLive,
      dvrWindowSeconds: details.dvr_window_seconds || null,
      eventStartAt: details.event_start_at || details.scheduled_at || null,
      countdownActive: false,
      failoverSource: details.failover_url || null,
      lowLatency: !!details.low_latency,
    },
    pipelinePhase: PipelinePhase.SESSION_INIT,
    analytics: {
      correlationId: null,
      sessionStartedAt: Date.now(),
    },
  });

  if (opts.bus) {
    session.bindBus(opts.bus);
  }

  return session;
}

export default createHostPlaybackSession;
